import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { quietly, writesSoFar } from "./api/client";
import { todayNo } from "./customers";

/**
 * A control whose truth lives on the server but must react instantly to a
 * click: the local value wins until the server value changes (which normally
 * means our write landed and the next poll confirmed it).
 */
export function useMirrored<T>(server: T): [T, (v: T | undefined) => void] {
  const [local, setLocal] = useState<T | undefined>(undefined);
  useEffect(() => {
    setLocal(undefined);
  }, [server]);
  return [local !== undefined ? local : server, setLocal];
}

/**
 * Runs `fn` every `ms` while `enabled`. The callback is kept in a ref so a new
 * closure each render does not restart the timer (which would reset the beat
 * and, with a 15s beat, could starve the poll entirely).
 *
 * Re-enabling also fires once immediately — without it, hopping between tabs
 * more often than the interval means the data never refreshes at all.
 * `skipFirst` suppresses that for the initial mount, where the view has just
 * loaded its data anyway.
 *
 * What it reads is a refresh, never a wait: the line along the top stays dark (client.ts quietly).
 */
export function useInterval(
  fn: () => void,
  ms: number,
  enabled: boolean,
  opts: { fireOnEnable?: boolean } = {},
): void {
  const saved = useRef(fn);
  const mounted = useRef(false);
  const { fireOnEnable = true } = opts;
  useEffect(() => {
    saved.current = fn;
  }, [fn]);
  useEffect(() => {
    if (!enabled) return;
    if (fireOnEnable && mounted.current) quietly(saved.current);
    mounted.current = true;
    const id = window.setInterval(() => quietly(saved.current), ms);
    return () => window.clearInterval(id);
  }, [ms, enabled, fireOnEnable]);
}

/**
 * Fires when the tab becomes visible again. Background tabs have their timers
 * throttled to a minute or more, so without this a tab left open in the
 * background shows stale data for as long as it takes the next tick to land.
 */
export function useOnVisible(fn: () => void, enabled: boolean): void {
  const saved = useRef(fn);
  useEffect(() => {
    saved.current = fn;
  }, [fn]);
  useEffect(() => {
    if (!enabled) return;
    const onVis = () => {
      if (!document.hidden) quietly(saved.current);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [enabled]);
}

/**
 * Fires when the page comes back in front of somebody: the tab shown again (a
 * phone back from another app, another tab clicked back) or the window
 * focused again (a desktop clicked back into, which leaves the tab visible
 * the whole time, so visibilitychange never fires). A switch pressed on one
 * device used to stay as it was on the other until somebody refreshed
 * (owner, 2026-10-21: 「两个设备按开关是不同步的要刷新才看得见」).
 *
 * At most once in minGapMs: coming back fires both events, and clicking in
 * and out of a window should not ask the server each time. A page someone
 * keeps watching needs a beat as well: on 对话 a press on another screen
 * waited for the next 15-second read (2026-10-28), so it asks every few
 * seconds whether anything changed (LeadsView PULSE_MS).
 */
export function useOnReturn(fn: () => void, enabled: boolean, minGapMs = 3000): void {
  const saved = useRef(fn);
  const last = useRef(0);
  useEffect(() => {
    saved.current = fn;
  }, [fn]);
  useEffect(() => {
    if (!enabled) return;
    const back = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - last.current < minGapMs) return;
      last.current = now;
      quietly(saved.current);
    };
    document.addEventListener("visibilitychange", back);
    window.addEventListener("focus", back);
    return () => {
      document.removeEventListener("visibilitychange", back);
      window.removeEventListener("focus", back);
    };
  }, [enabled, minGapMs]);
}

/**
 * Whether what a page read is still good enough to come back to without
 * reading it again: less than ten seconds old, and nothing written from this
 * tab since. Going back and forth between pages read the page again on every
 * return — 跟进 3.6–4.9 s each time on production, and five presses between
 * 跟进 and 对话 sent eleven requests on staging (2026-10-21). The page's own
 * refresh and its 重新读 still read whenever they run.
 */
export function useFreshRead(ms = 10_000): { done: () => void; fresh: () => boolean } {
  const last = useRef({ at: 0, writes: -1 });
  return useMemo(
    () => ({
      done: () => {
        last.current = { at: Date.now(), writes: writesSoFar() };
      },
      fresh: () => Date.now() - last.current.at < ms && writesSoFar() === last.current.writes,
    }),
    [ms],
  );
}

/** Debounces a fast-changing value (search box) before it drives a request. */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/**
 * Guards against out-of-order responses: every request takes a ticket, and a
 * result is only accepted if no newer request has been issued since. Without
 * it a slow reply for old filters can overwrite the list the operator is
 * actually looking at.
 */
export function useLatest(): { next: () => number; isCurrent: (t: number) => boolean } {
  const seq = useRef(0);
  // The returned object MUST keep a stable identity: callers put it in
  // useCallback deps, and a fresh object each render would invalidate the
  // callback, re-run the effect that calls it, and spin a request loop.
  const ref = useRef({
    next: () => ++seq.current,
    isCurrent: (t: number) => t === seq.current,
  });
  return ref.current;
}

// One timer for every clock on screen, so 「5 分钟前」 and 今天 move on by
// themselves: the long lists they sit in are not redrawn to update them.
const clockSubs = new Set<() => void>();
let clockTimer: number | undefined;
function onClock(fn: () => void): () => void {
  clockSubs.add(fn);
  clockTimer ??= window.setInterval(() => clockSubs.forEach((f) => f()), 5000);
  return () => {
    clockSubs.delete(fn);
    if (!clockSubs.size && clockTimer !== undefined) {
      window.clearInterval(clockTimer);
      clockTimer = undefined;
    }
  };
}
// Half-minutes: 「26 分钟」 is rounded, so it can turn at any second, and the
// table's 30-second refresh used to redraw it that often.
const halfMinuteNow = () => Math.floor(Date.now() / 30000);

/** The time to the half-minute; a component using it redraws every 30 seconds. */
export const useClock = (): number => useSyncExternalStore(onClock, halfMinuteNow);

/** Today on the shop's calendar; a component using it redraws at midnight. */
export const useToday = (): number => useSyncExternalStore(onClock, todayNo);
